import React, { useEffect, useState } from 'react';
import { Alert, Form, Spinner, Table } from 'react-bootstrap';
import { getEventRelatedObjects } from 'api/events/endpoints/getEventRelatedObjects';
import type { RelatedObject } from 'api/events/endpoints/getEventRelatedObjects';
import { byTypeThenName, objectName, objectType, rowKey, runsChild, usage } from './relatedObjectDisplay';
import { acceptDegraded } from 'validation';

// The panel answering "which objects use this event, and how", on the event
// dialog. It applies to every event type: a file event is if anything the case
// where it matters most, since the arrival raises the event and this is what
// names the definitions that then run.

interface EventRelatedObjectsProps {
  eventId: number;
}

const EventRelatedObjects: React.FC<EventRelatedObjectsProps> = ({ eventId }) => {
  const [objects, setObjects] = useState<readonly RelatedObject[]>([]);
  // Starts true. The first render runs before the effect, so starting false
  // shows "Nothing uses this event" for a frame on every open — a false
  // negative to exactly the question the panel is asked.
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = acceptDegraded(await getEventRelatedObjects(eventId));
        if (current) setObjects(response.related_objects);
      } catch {
        // The API layer emits machine-readable problems; the wording is the
        // caller's, and one unreadable panel must not imply the event is unused.
        if (current) {
          setObjects([]);
          setError('Failed to load related objects.');
        }
      } finally {
        if (current) setLoading(false);
      }
    };
    load();
    return () => {
      current = false;
    };
  }, [eventId]);

  const sorted = [...objects].sort(byTypeThenName);

  return (
    <Form.Group className="mt-3">
      <Form.Label>Related Objects</Form.Label>

      {loading && (
        <div className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          <span>Loading related objects...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="warning" className="mb-0">
          {error}
        </Alert>
      )}

      {!loading && !error && sorted.length === 0 && <div className="text-muted">Nothing uses this event.</div>}

      {!loading && !error && sorted.length > 0 && (
        <Table striped bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Object</th>
              <th>Type</th>
              <th>Zone</th>
              <th>Uses this event</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((object, index) => (
              <tr key={rowKey(object, index)}>
                <td>
                  {objectName(object)}
                  {runsChild(object) && <div className="text-muted small">{runsChild(object)}</div>}
                </td>
                <td>{objectType(object)}</td>
                <td>{object.zone_name ?? '—'}</td>
                <td>{usage(object)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Form.Group>
  );
};

export default EventRelatedObjects;
